import React, { useCallback, useMemo } from "react";
import Menu from "@ui/Menu";
import { ArrowDownSLine, ArrowUpSLine, ExternalLinkLine, LinkM } from "@ui/IconV2/react";
import OptionList from "@ui/OptionList";
import { FragmentSlot, OptionV2 } from "@ui/Option/v2";
import formatDate from "@ui/i18n/formatDate";
import { css } from "@linaria/core";
import tui from "@ui/i18n";
import { colors, spacings } from "@ui/Theme";
import Text from "@ui/Text";
import Ellipsis from "@ui/Ellipsis";
import ButtonIconV2 from "@ui/ButtonIconV2";
import t from "../../../../../../../i18n";
import { useLocation } from "react-router-dom";
import { toPath } from "../../../../../../../router/helpers/path";
import { useSummaryDashboards } from "../../../../../../../api/useSummaryDashboards";
import Link from "@ui/Link";
import { color } from "@ui";
import { toBasenamePath } from "../../../../../../../router/helpers/navigation";
import { useEventsDashboards } from "../../../../../../../api/events/dashboards";

export const DashboardUsage = ({
  projectId,
  dashboardId,
  isPrivate,
}: {
  projectId: string;
  dashboardId: string;
  isPrivate: boolean;
}) => {
  const location = useLocation();
  const [open, setOpen] = React.useState(false);

  const close = useCallback(() => setOpen(s => !s), []);

  const summaryDashboards = useSummaryDashboards(projectId);
  const isLinkedToHome = summaryDashboards.some(
    summaryDashboard => summaryDashboard.dashboardId === dashboardId,
  );

  const eventsDashboards = useEventsDashboards({ projectId });
  const dashboardEvents = useMemo(
    () => eventsDashboards.filter(eventDashboard => eventDashboard.dashboardId === dashboardId),
    [eventsDashboards, dashboardId],
  );

  if (
    (dashboardEvents.length === 0 && !isLinkedToHome) ||
    location.pathname.includes("fault-event")
  )
    return null;

  if (dashboardEvents.length === 0 && isLinkedToHome)
    return (
      <div className={eventsLink}>
        <LinkM />
        <Text type="body1">
          {t("dashboard.linked_event.menu.content", {
            linkedEventCount: 0,
            linkedToHome: (
              <Link
                className={styledLink}
                to={toPath("/projects/:projectId/summary/dashboard/:dashboardId", {
                  projectId,
                  dashboardId,
                })}
                target="_blank"
              >
                {t("dashboard.linked_event.menu.content.home_page")}
              </Link>
            ),
            isPrivate,
          })}
        </Text>
      </div>
    );

  return (
    <Menu
      className={faultEventMenu}
      opened={open}
      setOpened={close}
      placement="bottom-end"
      overlay={
        <OptionList onRequestClose={close}>
          {dashboardEvents.length === 0 && <div className={blankState}>{tui("filter.none")}</div>}
          {dashboardEvents.length > 0 &&
            dashboardEvents.map(dashboardEvent => {
              const path = dashboardEvent.eventFaultName
                ? toPath("/projects/:projectId/fault-events/:faultEventId/dashboard/:dashboardId", {
                    projectId: dashboardEvent.projectId,
                    faultEventId: dashboardEvent.faultEventId,
                    dashboardId,
                  })
                : dashboardEvent.eventId
                  ? toPath(
                      "/projects/:projectId/fault-events/manual/:manualFaultEventId/dashboard/:dashboardId",
                      {
                        projectId: dashboardEvent.projectId,
                        manualFaultEventId: dashboardEvent.eventId,
                        dashboardId: dashboardEvent.dashboardId,
                      },
                    )
                  : undefined;

              return (
                <OptionV2
                  key={`${dashboardEvent.eventName}-${dashboardEvent.eventStartsAt}-${dashboardEvent.eventEndsAt}`}
                  className={labelOption}
                  href={path ? toBasenamePath(path) : undefined}
                  target="_blank"
                  closeOnClick
                >
                  <Ellipsis slot="text" maxNumberOfLines={2}>
                    {dashboardEvent.eventName}
                  </Ellipsis>
                  <FragmentSlot slot="extra">
                    <ExternalLinkLine color={colors.carbon800} size="xs" />
                  </FragmentSlot>
                  <FragmentSlot slot="description">
                    {formatDate(dashboardEvent.eventStartsAt, "slash", null)} —{" "}
                    {dashboardEvent.eventEndsAt
                      ? formatDate(dashboardEvent.eventEndsAt, "slash", null)
                      : null}
                  </FragmentSlot>
                </OptionV2>
              );
            })}
        </OptionList>
      }
    >
      <div className={eventsLink}>
        <LinkM />
        <Text type="body1">
          {t("dashboard.linked_event.menu.content", {
            linkedEventCount: dashboardEvents.length,
            linkedToHome: isLinkedToHome ? (
              <Link
                className={styledLink}
                to={toBasenamePath(
                  toPath("/projects/:projectId/summary/dashboard/:dashboardId", {
                    projectId,
                    dashboardId,
                  }),
                )}
                target="_blank"
              >
                {t("dashboard.linked_event.menu.content.home_page")}
              </Link>
            ) : undefined,
            isPrivate,
          })}
        </Text>
        <ButtonIconV2>
          {open ? <ArrowUpSLine size="xs" /> : <ArrowDownSLine size="xs" />}
        </ButtonIconV2>
      </div>
    </Menu>
  );
};

// #region CSS
const eventsLink = css`
  cursor: pointer;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: ${spacings.xxs};
`;

const faultEventMenu = css`
  max-width: 300px;
  max-height: 250px;
`;

const labelOption = css`
  & > * {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    @supports (-webkit-line-clamp: 2) {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: initial;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
  }
`;

const blankState = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${spacings.xs} 10px;
  flex: 1;
  font-weight: 600;
`;

const styledLink = css`
  display: inline-flex;
  align-items: center;
  color: ${color.lightNitrogen500_Main};
  :hover {
    text-decoration: underline;
    color: ${color.lightNitrogen600};
  }
`;

// #endregion CSS
